'use client';

import {
    Avatar,
    Button,
    Chip,
    Divider,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ScrollShadow,
    Spinner,
    Switch,
    Tooltip,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { QUERY_CONSTANTS, QUERY_KEYS } from '@/@studio-ai/constants';
import type { ShareModalProps } from '@/@studio-ai/props';
import { studioAIService } from '@/@studio-ai/services';
import type { ConversationShareDetails, OrgUserBasic, ShareRecord } from '@/@studio-ai/types';
import { getViewAsSnapshot, hasViewAsSession, subscribeViewAs } from '@/@subsidiary/view-as-store';

/** Why the link switch is off (M4S-5670 §5). */
export const LINK_SHARE_BLOCKED_COPY = 'Link sharing is off for conversations run as a client';

const tabHoldsViewAsSession = () => hasViewAsSession(getViewAsSnapshot());

export function ShareModal({
    isOpen,
    onClose,
    conversationId,
    conversationTitle,
    organizationUsers,
    onShare,
    onGetShares,
    onRevokeShare,
    reportId,
    masqueradeOrgId: masqueradeOrgIdHint,
}: ShareModalProps) {
    // Loaded data
    const [shareDetails, setShareDetails] = useState<ConversationShareDetails | null>(null);
    const [isLoadingShares, setIsLoadingShares] = useState(false);

    // The `organizationUsers` prop is only populated on /dani/history, where
    // `HistoryQueriesEnabler` turns on the gated queries in useHistoryOperations.
    // Everywhere else it arrives empty, leaving the people picker with nothing to
    // match — so fetch it here on open instead. Same query key as the gated query,
    // so on the history page this resolves from cache with no extra request.
    // Mirrors the pattern in persistent-assistant/conversation-history-popover.tsx.
    // See BT-2010.
    const { data: fetchedOrgUsers, isLoading: isLoadingOrgUsers } = useQuery({
        queryKey: QUERY_KEYS.ORGANIZATION_USERS,
        queryFn: () => studioAIService.getOrganizationUsers(),
        enabled: isOpen,
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
    });

    const availableUsers = fetchedOrgUsers ?? organizationUsers;

    // "Anyone with the link" reaches any signed-in Studio user, in any org, so a
    // conversation run as a client is never link-shared (M4S-5670 §5). The
    // modal decides this itself for every caller: it resolves the
    // conversation's client tag, and fails closed while that lookup is loading
    // or has failed. A caller's tag is a hint that can only add the block.
    const tagQuery = useQuery({
        queryKey: QUERY_KEYS.CONVERSATION_HISTORY(conversationId),
        queryFn: () => studioAIService.getConversationHistory(conversationId),
        enabled: isOpen && !!conversationId,
        retry: false,
    });
    const isViewingAsClient = useSyncExternalStore(
        subscribeViewAs,
        tabHoldsViewAsSession,
        () => false,
    );
    const isLinkShareBlocked =
        isViewingAsClient ||
        !!masqueradeOrgIdHint ||
        tagQuery.isPending ||
        tagQuery.isError ||
        !!tagQuery.data?.masqueradeOrgId;

    // Pending local state (committed only on Save)
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<OrgUserBasic[]>([]);
    const [shareMessage, setShareMessage] = useState('');
    const [pendingOrgShare, setPendingOrgShare] = useState(false);
    const [pendingLinkShare, setPendingLinkShare] = useState(false);
    const [pendingRevokeIds, setPendingRevokeIds] = useState<Set<number>>(new Set());

    // Snapshot of the initial toggle states so we can diff on save
    const [initialOrgShare, setInitialOrgShare] = useState(false);
    const [initialLinkShare, setInitialLinkShare] = useState(false);

    // UI state
    const [isSaving, setIsSaving] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // ── Bootstrap / teardown ────────────────────────────────────────────
    useEffect(() => {
        if (isOpen && conversationId) {
            setIsLoadingShares(true);
            setError(null);
            onGetShares(conversationId)
                .then((details) => {
                    setShareDetails(details);
                    const orgActive =
                        details.shares?.some((s) => s.shareType === 'organization') ?? false;
                    const linkActive = details.hasLinkShare ?? false;
                    setPendingOrgShare(orgActive);
                    setPendingLinkShare(linkActive);
                    setInitialOrgShare(orgActive);
                    setInitialLinkShare(linkActive);
                })
                .catch(() => {
                    setShareDetails(null);
                    setError('Failed to load sharing details. Please try again.');
                })
                .finally(() => setIsLoadingShares(false));
        }
        if (!isOpen) {
            setSearchQuery('');
            setSelectedUsers([]);
            setShareMessage('');
            setError(null);
            setSuccessMessage(null);
            setLinkCopied(false);
            setPendingRevokeIds(new Set());
        }
    }, [isOpen, conversationId, onGetShares]);

    // ── Derived values ──────────────────────────────────────────────────

    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return [];
        const query = searchQuery.toLowerCase();
        const alreadySharedIds = new Set(
            shareDetails?.shares
                ?.filter(
                    (s) =>
                        s.shareType === 'user' && s.sharedWithUser && !pendingRevokeIds.has(s.id),
                )
                .map((s) => s.sharedWithUser!.orgUserId) ?? [],
        );
        const selectedIds = new Set(selectedUsers.map((u) => u.orgUserId));

        return availableUsers.filter(
            (user) =>
                !alreadySharedIds.has(user.orgUserId) &&
                !selectedIds.has(user.orgUserId) &&
                (`${user.firstName} ${user.lastName}`.toLowerCase().includes(query) ||
                    user.email.toLowerCase().includes(query)),
        );
    }, [searchQuery, availableUsers, shareDetails, selectedUsers, pendingRevokeIds]);

    const visibleUserShares = useMemo(
        () =>
            shareDetails?.shares.filter(
                (s) => s.shareType === 'user' && !pendingRevokeIds.has(s.id),
            ) ?? [],
        [shareDetails, pendingRevokeIds],
    );

    const shareLink = useMemo(() => {
        // No link to copy while link sharing is blocked, even an existing one.
        if (!pendingLinkShare || isLinkShareBlocked) return '';
        const basePath = reportId
            ? `/dani/${conversationId}/${reportId}`
            : `/dani/${conversationId}`;
        return `${window.location.origin}${basePath}?shared=true`;
    }, [pendingLinkShare, isLinkShareBlocked, conversationId, reportId]);

    const hasChanges = useMemo(
        () =>
            selectedUsers.length > 0 ||
            pendingOrgShare !== initialOrgShare ||
            pendingLinkShare !== initialLinkShare ||
            pendingRevokeIds.size > 0,
        [
            selectedUsers,
            pendingOrgShare,
            initialOrgShare,
            pendingLinkShare,
            initialLinkShare,
            pendingRevokeIds,
        ],
    );

    // ── Handlers ────────────────────────────────────────────────────────

    const handleSelectUser = useCallback((user: OrgUserBasic) => {
        setSelectedUsers((prev) => [...prev, user]);
        setSearchQuery('');
    }, []);

    const handleRemoveSelectedUser = useCallback((userId: number) => {
        setSelectedUsers((prev) => prev.filter((u) => u.orgUserId !== userId));
    }, []);

    const handleMarkForRevoke = useCallback((share: ShareRecord) => {
        setPendingRevokeIds((prev) => new Set(prev).add(share.id));
    }, []);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            // 1. Share with newly-selected users
            if (selectedUsers.length > 0) {
                await onShare(conversationId, {
                    shareType: 'user',
                    userIds: selectedUsers.map((u) => u.orgUserId),
                    message: shareMessage || undefined,
                });
            }

            // 2. Org share toggle
            if (pendingOrgShare !== initialOrgShare) {
                if (pendingOrgShare) {
                    await onShare(conversationId, { shareType: 'organization' });
                } else {
                    const orgShare = shareDetails?.shares.find(
                        (s) => s.shareType === 'organization',
                    );
                    if (orgShare) {
                        await onRevokeShare(orgShare.id);
                    }
                }
            }

            // 3. Link share toggle. While link sharing is blocked an existing
            // link can be turned off, never on.
            if (pendingLinkShare !== initialLinkShare) {
                if (pendingLinkShare) {
                    if (isLinkShareBlocked) throw new Error(LINK_SHARE_BLOCKED_COPY);
                    await onShare(conversationId, { shareType: 'link' });
                } else {
                    const linkShare = shareDetails?.shares.find((s) => s.shareType === 'link');
                    if (linkShare) {
                        await onRevokeShare(linkShare.id);
                    }
                }
            }

            // 4. Revoke individual user shares
            const revokeArray = Array.from(pendingRevokeIds);
            for (const shareId of revokeArray) {
                await onRevokeShare(shareId);
            }

            // Refresh share state from API and re-snapshot
            const updated = await onGetShares(conversationId);
            setShareDetails(updated);

            const orgActive = updated.shares?.some((s) => s.shareType === 'organization') ?? false;
            const linkActive = updated.hasLinkShare ?? false;
            setPendingOrgShare(orgActive);
            setPendingLinkShare(linkActive);
            setInitialOrgShare(orgActive);
            setInitialLinkShare(linkActive);
            setPendingRevokeIds(new Set());
            setSelectedUsers([]);
            setShareMessage('');

            setSuccessMessage('Sharing settings saved');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save sharing settings');
        } finally {
            setIsSaving(false);
        }
    }, [
        selectedUsers,
        shareMessage,
        pendingOrgShare,
        initialOrgShare,
        pendingLinkShare,
        initialLinkShare,
        pendingRevokeIds,
        shareDetails,
        conversationId,
        onShare,
        onGetShares,
        onRevokeShare,
        isLinkShareBlocked,
    ]);

    const handleCopyLink = useCallback(() => {
        if (shareLink) {
            navigator.clipboard.writeText(shareLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        }
    }, [shareLink]);

    const getUserInitials = (firstName: string, lastName: string) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
    };

    // ── Render ──────────────────────────────────────────────────────────

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={(open) => !open && onClose()}
            size="lg"
            classNames={{
                base: 'bg-content1',
                header: 'border-b border-divider',
                body: 'py-4',
                footer: 'border-t border-divider',
            }}
        >
            <ModalContent>
                {(onModalClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Icon icon="lucide:share-2" className="text-primary" width={20} />
                                <span>
                                    Share &ldquo;{conversationTitle || 'Conversation'}&rdquo;
                                </span>
                            </div>
                        </ModalHeader>

                        <ModalBody>
                            {isLoadingShares ? (
                                <div className="flex items-center justify-center py-8">
                                    <Spinner size="md" />
                                </div>
                            ) : (
                                <div className="space-y-5">
                                    {/* Error / Success Messages */}
                                    {error && (
                                        <div className="flex items-center gap-2 rounded-lg bg-danger-50 p-3 text-sm text-danger">
                                            <Icon icon="lucide:alert-circle" width={16} />
                                            {error}
                                        </div>
                                    )}
                                    {successMessage && (
                                        <div className="flex items-center gap-2 rounded-lg bg-success-50 p-3 text-sm text-success">
                                            <Icon icon="lucide:check-circle" width={16} />
                                            {successMessage}
                                        </div>
                                    )}

                                    {/* ── Add People ────────────────────────── */}
                                    <div>
                                        <p className="mb-2 text-sm font-medium text-foreground-600">
                                            Add people
                                        </p>
                                        <div className="relative">
                                            <Input
                                                placeholder="Search by name or email..."
                                                value={searchQuery}
                                                onValueChange={setSearchQuery}
                                                startContent={
                                                    <Icon
                                                        icon="lucide:search"
                                                        className="text-default-400"
                                                        width={16}
                                                    />
                                                }
                                                size="sm"
                                                variant="bordered"
                                                classNames={{
                                                    inputWrapper: 'border-default-200',
                                                }}
                                            />
                                            {searchQuery.trim() &&
                                                filteredUsers.length === 0 &&
                                                isLoadingOrgUsers && (
                                                    <div className="absolute left-0 right-0 top-full z-50 mt-1 flex items-center gap-2 rounded-lg border border-default-200 bg-content1 px-3 py-2 shadow-lg">
                                                        <Spinner size="sm" />
                                                        <span className="text-tiny text-default-500">
                                                            Loading people…
                                                        </span>
                                                    </div>
                                                )}
                                            {filteredUsers.length > 0 && searchQuery.trim() && (
                                                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-default-200 bg-content1 shadow-lg">
                                                    {filteredUsers.slice(0, 8).map((user) => (
                                                        <Button
                                                            key={user.orgUserId}
                                                            variant="light"
                                                            className="flex h-auto w-full items-center justify-start gap-3 px-3 py-2"
                                                            onPress={() => handleSelectUser(user)}
                                                        >
                                                            <Avatar
                                                                name={getUserInitials(
                                                                    user.firstName,
                                                                    user.lastName,
                                                                )}
                                                                size="sm"
                                                                classNames={{
                                                                    base: 'bg-primary/10 text-primary text-xs shrink-0',
                                                                }}
                                                            />
                                                            <div className="min-w-0 flex-1 text-left">
                                                                <p className="truncate text-sm font-medium">
                                                                    {user.firstName} {user.lastName}
                                                                </p>
                                                                <p className="truncate text-xs text-default-400">
                                                                    {user.email}
                                                                </p>
                                                            </div>
                                                        </Button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {selectedUsers.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {selectedUsers.map((user) => (
                                                    <Chip
                                                        key={user.orgUserId}
                                                        onClose={() =>
                                                            handleRemoveSelectedUser(user.orgUserId)
                                                        }
                                                        variant="flat"
                                                        size="sm"
                                                        avatar={
                                                            <Avatar
                                                                name={getUserInitials(
                                                                    user.firstName,
                                                                    user.lastName,
                                                                )}
                                                                size="sm"
                                                                classNames={{
                                                                    base: 'bg-primary/10 text-primary text-[10px]',
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        {user.firstName} {user.lastName}
                                                    </Chip>
                                                ))}
                                            </div>
                                        )}

                                        {selectedUsers.length > 0 && (
                                            <Input
                                                placeholder="Add a message (optional)"
                                                value={shareMessage}
                                                onValueChange={setShareMessage}
                                                size="sm"
                                                variant="bordered"
                                                className="mt-2"
                                                classNames={{
                                                    inputWrapper: 'border-default-200',
                                                }}
                                            />
                                        )}
                                    </div>

                                    {/* ── People with Access ────────────────── */}
                                    {visibleUserShares.length > 0 && (
                                        <>
                                            <Divider />
                                            <div>
                                                <p className="mb-2 text-sm font-medium text-foreground-600">
                                                    People with access
                                                </p>
                                                <ScrollShadow className="max-h-40">
                                                    <div className="space-y-1">
                                                        {shareDetails?.owner && (
                                                            <div className="flex items-center gap-3 rounded-lg px-2 py-1.5">
                                                                <Avatar
                                                                    name={getUserInitials(
                                                                        shareDetails.owner
                                                                            .firstName,
                                                                        shareDetails.owner.lastName,
                                                                    )}
                                                                    size="sm"
                                                                    classNames={{
                                                                        base: 'bg-primary text-white text-xs',
                                                                    }}
                                                                />
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="truncate text-sm font-medium">
                                                                        {
                                                                            shareDetails.owner
                                                                                .firstName
                                                                        }{' '}
                                                                        {
                                                                            shareDetails.owner
                                                                                .lastName
                                                                        }
                                                                    </p>
                                                                    <p className="truncate text-xs text-default-400">
                                                                        {shareDetails.owner.email}
                                                                    </p>
                                                                </div>
                                                                <Chip
                                                                    size="sm"
                                                                    variant="flat"
                                                                    color="primary"
                                                                    classNames={{
                                                                        base: 'h-6',
                                                                    }}
                                                                >
                                                                    Owner
                                                                </Chip>
                                                            </div>
                                                        )}

                                                        {visibleUserShares.map((share) => (
                                                            <div
                                                                key={share.id}
                                                                className="group flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-default-100"
                                                            >
                                                                <Avatar
                                                                    name={getUserInitials(
                                                                        share.sharedWithUser
                                                                            ?.firstName || '',
                                                                        share.sharedWithUser
                                                                            ?.lastName || '',
                                                                    )}
                                                                    size="sm"
                                                                    classNames={{
                                                                        base: 'bg-secondary/10 text-secondary text-xs',
                                                                    }}
                                                                />
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="truncate text-sm font-medium">
                                                                        {
                                                                            share.sharedWithUser
                                                                                ?.firstName
                                                                        }{' '}
                                                                        {
                                                                            share.sharedWithUser
                                                                                ?.lastName
                                                                        }
                                                                    </p>
                                                                    <p className="truncate text-xs text-default-400">
                                                                        {
                                                                            share.sharedWithUser
                                                                                ?.email
                                                                        }
                                                                    </p>
                                                                </div>
                                                                <Tooltip content="Remove access">
                                                                    <Button
                                                                        isIconOnly
                                                                        size="sm"
                                                                        variant="light"
                                                                        className="opacity-0 group-hover:opacity-100"
                                                                        onPress={() =>
                                                                            handleMarkForRevoke(
                                                                                share,
                                                                            )
                                                                        }
                                                                    >
                                                                        <Icon
                                                                            icon="lucide:x"
                                                                            width={14}
                                                                            className="text-default-400"
                                                                        />
                                                                    </Button>
                                                                </Tooltip>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </ScrollShadow>
                                            </div>
                                        </>
                                    )}

                                    {/* ── General Access ────────────────────── */}
                                    <Divider />
                                    <div>
                                        <p className="mb-3 text-sm font-medium text-foreground-600">
                                            General access
                                        </p>
                                        <div className="space-y-3">
                                            {/* Organization toggle */}
                                            <div className="flex items-center gap-3 rounded-lg border border-default-200 p-3 transition-colors hover:bg-default-50">
                                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-warning/10">
                                                    <Icon
                                                        icon="lucide:building-2"
                                                        className="text-warning"
                                                        width={16}
                                                    />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium">
                                                        Everyone in your organization
                                                    </p>
                                                    <p className="text-xs text-default-400">
                                                        All members can view this conversation
                                                    </p>
                                                </div>
                                                <Switch
                                                    size="sm"
                                                    isSelected={pendingOrgShare}
                                                    onValueChange={setPendingOrgShare}
                                                    aria-label="Share with organization"
                                                />
                                            </div>

                                            {/* Link toggle */}
                                            <div className="rounded-lg border border-default-200 p-3 transition-colors hover:bg-default-50">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success/10">
                                                        <Icon
                                                            icon="lucide:link"
                                                            className="text-success"
                                                            width={16}
                                                        />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium">
                                                            Anyone with the link
                                                        </p>
                                                        <p className="text-xs text-default-400">
                                                            {isLinkShareBlocked
                                                                ? LINK_SHARE_BLOCKED_COPY
                                                                : 'Anyone signed in to Studio with the link can view this conversation, in any organization.'}
                                                        </p>
                                                    </div>
                                                    <Switch
                                                        size="sm"
                                                        isSelected={pendingLinkShare}
                                                        onValueChange={setPendingLinkShare}
                                                        // Blocked: an existing link can be turned off, never on.
                                                        isDisabled={
                                                            isLinkShareBlocked && !pendingLinkShare
                                                        }
                                                        aria-label="Share with link"
                                                    />
                                                </div>

                                                {pendingLinkShare && shareLink && (
                                                    <div className="mt-3 flex items-center gap-2">
                                                        <div className="min-w-0 flex-1 truncate rounded-lg bg-default-100 px-3 py-2 font-mono text-xs text-default-600">
                                                            {shareLink}
                                                        </div>
                                                        <Tooltip
                                                            content={
                                                                linkCopied ? 'Copied!' : 'Copy link'
                                                            }
                                                        >
                                                            <Button
                                                                isIconOnly
                                                                size="sm"
                                                                variant="flat"
                                                                color={
                                                                    linkCopied
                                                                        ? 'success'
                                                                        : 'default'
                                                                }
                                                                onPress={handleCopyLink}
                                                            >
                                                                <Icon
                                                                    icon={
                                                                        linkCopied
                                                                            ? 'lucide:check'
                                                                            : 'lucide:copy'
                                                                    }
                                                                    width={14}
                                                                />
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </ModalBody>

                        <ModalFooter>
                            <Button variant="light" size="sm" onPress={onModalClose}>
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                size="sm"
                                onPress={handleSave}
                                isLoading={isSaving}
                                isDisabled={!hasChanges}
                                startContent={!isSaving && <Icon icon="lucide:save" width={14} />}
                            >
                                Save
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
}
